/* oxlint-disable */
"use client"

import Image from "next/image"
import React, { useState, useEffect, useRef } from "react"

import { Card } from "@heroui/react"
import { CheckCircle2, Mail } from "lucide-react"

import { openUrlModal } from "@/components/modal/url"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet"
import { useToast } from "@/components/ui/use-toast"
import { emailOtp } from "@/core/auth/client/auth-client"

interface OtpVerificationSheetProps {
  isOpen: boolean
  onClose: () => void
  email: string
  onSuccess?: () => void
}

export function OtpVerificationSheet({
  isOpen,
  onClose,
  email,
  onSuccess
}: OtpVerificationSheetProps) {
  const [countdown, setCountdown] = useState(60)
  const [canResend, setCanResend] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [isVerified, setIsVerified] = useState(false)
  const [showCloseWarning, setShowCloseWarning] = useState(false)
  const [otp, setOtp] = useState(["", "", "", "", "", ""])
  const [error, setError] = useState("")
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const justCanceledWarningRef = useRef(false)
  const { toast } = useToast()

  useEffect(() => {
    if (!isOpen || canResend) return

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setCanResend(true)
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => {
      clearInterval(timer)
    }
  }, [isOpen, canResend])

  const handleSendOtp = async () => {
    try {
      setIsResending(true)

      // Use better-auth's emailOTP plugin to send OTP
      const result = await emailOtp.sendVerificationOtp({
        email,
        type: "email-verification"
      })

      if (result.error) {
        console.error("OTP Send Error:", result.error)
        toast({
          title: result.error.message || "Error al enviar código",
          variant: "destructive"
        })
      } else {
        setCountdown(60)
        setCanResend(false)
      }
    } catch (error) {
      console.error("OTP Send Exception:", error)
      toast({ title: "Error al enviar código", variant: "destructive" })
    } finally {
      setIsResending(false)
    }
  }

  useEffect(() => {
    if (isOpen && email) {
      handleSendOtp()
      setOtp(["", "", "", "", "", ""])
      setError("")
      setShowCloseWarning(false)
    }
  }, [isOpen])

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return // Only allow digits

    const newOtp = [...otp]
    newOtp[index] = value
    setOtp(newOtp)
    setError("")

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }

    // Auto-submit when all 6 digits are filled
    const otpCode = newOtp.join("")
    if (otpCode.length === 6) {
      handleVerifyOtp(otpCode)
    }
  }

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData("text").slice(0, 6)
    if (!/^\d+$/.test(pastedData)) return // Only allow digits

    const newOtp = pastedData.split("")
    while (newOtp.length < 6) {
      newOtp.push("")
    }
    setOtp(newOtp.slice(0, 6))

    // Auto-submit if pasted 6 digits
    if (pastedData.length === 6) {
      handleVerifyOtp(pastedData)
    }
  }

  const handleVerifyOtp = async (otpCode: string) => {
    try {
      setIsVerifying(true)

      const result = await emailOtp.verifyEmail({
        email,
        otp: otpCode
      })

      if (result.error) {
        console.error("OTP Verify Error:", result.error)
        setError("Código inválido")
        toast({
          title: result.error.message || "Código inválido",
          variant: "destructive"
        })
        // Reset OTP inputs on error
        setOtp(["", "", "", "", "", ""])
        inputRefs.current[0]?.focus()
      } else {
        setIsVerified(true)
        toast({ title: "Correo verificado exitosamente!", variant: "default" })

        setTimeout(() => {
          if (onSuccess) {
            onSuccess()
          } else {
            onClose()
            openUrlModal("onboarding")
          }
        }, 1500)
      }
    } catch (error) {
      console.error("OTP Verify Exception:", error)
      toast({ title: "Error al verificar código", variant: "destructive" })
      setOtp(["", "", "", "", "", ""])
      inputRefs.current[0]?.focus()
    } finally {
      setIsVerifying(false)
    }
  }

  const handleResend = async () => {
    setOtp(["", "", "", "", "", ""])
    setError("")
    await handleSendOtp()
  }

  const handleCloseAttempt = (open: boolean) => {
    // If trying to close (open = false)
    if (!open) {
      // If verified, allow close
      if (isVerified) {
        onClose()
        return
      }

      // If we just canceled the warning, ignore this close attempt
      if (justCanceledWarningRef.current) {
        justCanceledWarningRef.current = false
        return
      }

      // If warning is already showing, do nothing (prevents re-triggering)
      if (showCloseWarning) {
        return
      }

      // Show warning for unverified
      setShowCloseWarning(true)
    }
  }

  const handleCancelWarning = () => {
    setShowCloseWarning(false)
    justCanceledWarningRef.current = true
    // Reset the flag after a short delay
    setTimeout(() => {
      justCanceledWarningRef.current = false
    }, 300)
  }

  const handleSkipVerification = () => {
    setShowCloseWarning(false)
    onClose()
    openUrlModal("onboarding")
  }

  return (
    <>
      <Sheet open={isOpen} onOpenChange={handleCloseAttempt}>
        <SheetContent
          side="bottom"
          className="px-6 py-6 w-full mx-auto max-h-[90vh] border-sivarbet-border bg-sivarbet-background text-sivarbet-text rounded-t-2xl shadow-2xl"
        >
          <SheetHeader className="mb-6">
            <div className="flex items-center justify-center mb-4">
              {isVerified ? (
                <Card
                  isBlurred
                  className="w-16 h-16 rounded-full bg-default-500/20 border-2 border-default-500/30 flex items-center justify-center"
                >
                  <Image
                    src="/img/icons/mailCheck.svg"
                    alt="Mail"
                    width={34}
                    height={34}
                  />
                </Card>
              ) : (
                <Card
                  isBlurred
                  className="w-16 h-16 rounded-full bg-default-500/20 border-2 border-default-500/30 flex items-center justify-center"
                >
                  <Image
                    src="/img/icons/mail.svg"
                    alt="Mail"
                    width={34}
                    height={34}
                  />
                </Card>
              )}
            </div>
            <SheetTitle className="text-center text-xl font-semibold text-sivarbet-text">
              {isVerified ? "Correo Verificado!" : "Verificar tu Correo"}
            </SheetTitle>
            <SheetDescription className="text-center text-sm text-sivarbet-text/70">
              {isVerified
                ? "Tu correo ha sido verificado exitosamente"
                : `Ingresa el código de 6 dígitos enviado a ${email}`}
            </SheetDescription>
          </SheetHeader>

          {!isVerified && (
            <div className="space-y-6">
              <div className="space-y-4">
                <div
                  className="flex gap-2 justify-center"
                  onPaste={handlePaste}
                >
                  {otp.map((digit, index) => {
                    return (
                      <input
                        key={index}
                        ref={(el) => {
                          inputRefs.current[index] = el
                        }}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => {
                          handleOtpChange(index, e.target.value)
                        }}
                        onKeyDown={(e) => {
                          handleKeyDown(index, e)
                        }}
                        disabled={isVerifying}
                        className="w-12 h-14 text-center text-2xl font-semibold bg-sivarbet-border border-2 border-sivarbet-primary/30 rounded-lg focus:border-sivarbet-primary focus:outline-hidden disabled:opacity-50 transition-colors"
                      />
                    )
                  })}
                </div>
                {error && (
                  <p className="text-center text-sm text-red-500">{error}</p>
                )}
                {isVerifying && (
                  <p className="text-center text-sm text-sivarbet-text/70">
                    Verificando...
                  </p>
                )}
              </div>

              <div className="flex flex-col items-center gap-3">
                <div className="text-sm text-sivarbet-text/70">
                  {canResend ? (
                    <span>¿No recibiste el código?</span>
                  ) : (
                    <span>
                      Reenviar código en{" "}
                      <span className="font-semibold text-sivarbet-primary">
                        {countdown}s
                      </span>
                    </span>
                  )}
                </div>

                {canResend && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleResend}
                    disabled={isResending}
                    className="border-sivarbet-primary text-sivarbet-primary hover:bg-sivarbet-primary hover:text-white"
                  >
                    {isResending ? "Enviando..." : "Reenviar Código"}
                  </Button>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={showCloseWarning}
        onOpenChange={(open) => {
          if (!open) handleCancelWarning()
        }}
      >
        <AlertDialogContent className="bg-sivarbet-background border-sivarbet-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-sivarbet-text">
              ¿Saltar verificación de correo?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sivarbet-text/70">
              Puedes verificar tu correo más tarde desde la configuración de tu
              cuenta.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-sivarbet-border text-sivarbet-text"
              onClick={handleCancelWarning}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSkipVerification}
              className="bg-sivarbet-primary hover:bg-sivarbet-primary/90"
            >
              Continuar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
